// Core data types for procedural materials (Set9). Recipes are declarative layer stacks over
// shared GLSL primitives; params are the UI surface (Phase 7 sliders). See the Set9 plan doc.
// ParamKind — Set14 P2 widened the SHARED union for the scenery system's ez-tree knobs. The
// material system declares only scalar/color/ramp; the new arms (int/enum/bool/vec3) exist for
// scenery but cost the material system nothing (it never declares them). ⚠ Do NOT alter the
// existing arms — ParamValue feeds GLSL arg binding + UBO packing (UniformPacker.ts), so a
// change to an existing carrier type would ripple there.
export type ParamKind = "scalar" | "color" | "ramp" | "int" | "enum" | "bool" | "vec3";
export type ParamScalar = number;
export type ParamColor = [number, number, number];
/** Flat stop array: [t0, r0, g0, b0, t1, r1, g1, b1, ...] (t ascending, last t should be 1.0). */
export type ParamRamp = number[];
/** A vec3 carrier (e.g. `forceDirection`). Structurally a `[x, y, z]` triple, but a DISTINCT
 *  kind from `color` so the editor + swing semantics differ (color is a 0..1 tint; vec3 is a
 *  signed direction/vector in any range). Never swung (§4.3 a). */
export type ParamVec3 = [number, number, number];
export type ParamValue = ParamScalar | ParamColor | ParamRamp | boolean | string | ParamVec3;
export type ParamValueInRecipe = ParamValue | undefined; // undefined means user left unset (will fallback to default)
/** Reference to a param declared in the recipe's `params` list. Drives GLSL arg binding AND the UI sliders. */
export interface ParamRef { ref: string; }

/** ModRef combine op: `mul` → base*(1+m*amount) for LENGTHS/SCALES/DENSITIES (proportional, sign-preserving); `add` → base+m*amount for THRESHOLDS/ramp positions in [0,1] (a proportional change there would scale the swing by the base itself). */
export type ModOp = "mul" | "add";

/** Option D — a layer arg MODULATED by the recipe's macro modulator `g_macroMod` (see Primitives/MacroMod.glsl.ts). WHY: a STATIONARY noise field's AA colour is a constant, so wide zoom collapses to one colour; varying the layer's PARAMETERS with a slow field makes its local MEAN (a function of position) survive averaging. `base` = the value without modulation (literal or ParamRef, so it keeps using the normal param/UI/region machinery); `amount` = the swing (may be a ParamRef → the STRENGTH becomes a live slider; 0 = off); `op` = mul|add, default "mul". */
export interface ModRef { base: ParamValue | ParamRef; amount: ParamScalar | ParamRef; op?: ModOp; }

/** A layer arg: a literal (baked at assemble time), a ParamRef (UBO/region), or a ModRef (a base value modulated by the recipe's macro modulator). */
export type LayerArg = ParamValue | ParamRef | ModRef;
export function isParamRef(v: LayerArg): v is ParamRef { return typeof v == "object" && v != null && !Array.isArray(v) && "ref" in v; }
export function isModRef(v: LayerArg): v is ModRef { return typeof v == "object" && v != null && !Array.isArray(v) && "base" in v && "amount" in v; }

export type PrimitiveKind =
	| "hash" | "fbm" | "ramp" | "worley" | "scatter"
	| "waves" | "domainWarp" | "domainRepeat" | "mask" | "radialDistToWarpedAxis"
	| "bandedNoise" | "coverageField" | "geoHeight"
	// Wood (2026-08-24): the seam-free continuous wood surface — warped-axis growth rings +
	// anisotropic grain + knots with NO board lattice / inter-board gap (the planks domain
	// landed on its own `woodPlanks` recipe). Composes the EXISTING prelude helpers
	// (wavesVal/radialDistToWarpedAxis/fbmVal/hash01/rampVal), so it is additive (a
	// prelude-layer-body primitive; glsl:"" + implemented) — the terrain path and every
	// pre-existing built-in recipe emit byte-identical GLSL (the P6 regression gate).
	| "woodSurface"
	// Buildings Set1 §7.4 — building-surface primitives (brick coursing, siding laps, shingle
	// tabs, barrel tiles, standing seams). All compose the EXISTING prelude helpers, so they are
	// additive (a prelude-layer-body primitive; glsl:"" + implemented): the terrain path and
	// every pre-existing built-in recipe emit byte-identical GLSL (the P6 regression gate).
	| "brickBond" | "siding" | "shingles" | "barrelTiles" | "standingSeam";
export const PRIMITIVE_KINDS: PrimitiveKind[] = [
	"hash", "fbm", "ramp", "worley", "scatter",
	"waves", "domainWarp", "domainRepeat", "mask", "radialDistToWarpedAxis",
	"bandedNoise", "coverageField", "geoHeight",
	"woodSurface",
	"brickBond", "siding", "shingles", "barrelTiles", "standingSeam",
];

export type BlendMode = "over" | "multiply" | "add" | "maskedBy" | "heightBlend" | "lightModulatedRamp";
export const BLEND_MODES: BlendMode[] = ["over", "multiply", "add", "maskedBy", "heightBlend", "lightModulatedRamp"];

/** Option D — the per-recipe MACRO MODULATOR: a slow map-scale fbm evaluated ONCE per fragment per recipe (hoisted to the top of `eval_N`) as the GLSL global `g_macroMod`; layer args opt in via {@link ModRef}. Gives the material's local MEAN spatial structure so wide zoom shows real variation (see Primitives/MacroMod.glsl.ts). Per-RECIPE (not global) is the point: one shared field through each recipe's own ramps/thresholds is material-specific, unlike the single global ambient-detail overlay. Each FBM field is a `ParamScalar | ParamRef`, so every one is a live UI slider (a literal default keeps it baked; a ref makes it tunable). `wavelength` = coarsest octave in m; `octaves` bounds how fine it gets (shader floors the finest octave at MACRO_MOD_MIN_PX px — no alias, no normal break); `lacunarity`/`persistence` shape the fractal; `contrast` = output gain (an octave average has a small spread); `seedOffset` decorrelates recipes. */
export class MacroModConfig {
	constructor(data?: Partial<MacroModConfig>) { Object.assign(this, data); }
	/** Coarsest octave's cell size, in METRES. */
	wavelength: ParamScalar | ParamRef = 3000;
	/** Octave count (scalar-typed so it can be a ParamRef; cast to int in GLSL — the fbm octaves precedent). */
	octaves: ParamScalar | ParamRef = 4;
	lacunarity: ParamScalar | ParamRef = 2.6;
	persistence: ParamScalar | ParamRef = 0.62;
	/** Output gain applied after the octave average (see the class docblock). */
	contrast: ParamScalar | ParamRef = 2.2;
	/** Lattice offset that decorrelates this recipe's modulator from other recipes'. */
	seedOffset: ParamScalar | ParamRef = 0;
}

/** Which channels a layer contributes to: `both` (default), `color` (albedo only), `height`
 *  (height only — e.g. dirt's fine-fbm detail: relief without recolouring). Blend-mode height
 *  semantics: over + height = additive (detail layers); heightBlend = max (pebbles dominate where taller). */
export type LayerContribution = "both" | "color" | "height";

/** One exposed knob. Single source of truth for GLSL arg binding AND the auto-generated UI (mirror TerrainDefinition_ModUI).
 *  `regionModulated` params fetch their value from the per-region params texture (Phase 6) instead of a uniform. */
export class ParamSchema {
	constructor(data?: Partial<ParamSchema>) { Object.assign(this, data); }
	key = "";
	label = "";
	kind: ParamKind = "scalar";
	min = 0;
	max = 1;
	default: ParamValue = 0;
	regionModulated = false;
	/** Set14 P2 — the allowed values for an `enum` param, in display order. Absent = any string is
	 *  allowed (the editor falls back to a text input). The `value` is the STORED value (the flat
	 *  `ParamValue` string); `label` is what the UI shows. */
	options?: {value: string; label: string}[];
	/** Set14 P2 — the collapsible section this param's UI row belongs to (declaration order
	 *  within a group). Absent = "General". Drives KindEditor's grouping; the material system
	 *  never sets it and is unaffected. */
	group?: string;
}

/** One layer of a recipe: a single primitive instance + how it blends into the accumulated result.
 *  Layers carry stable string ids — NEVER positional indices (inserting a layer in a base would
 *  silently re-target every inheriting variant's overrides, see plan §5.1). */
export class MaterialLayer {
	constructor(data?: Partial<MaterialLayer>) { Object.assign(this, data); }
	id = "";
	primitive: PrimitiveKind = "fbm";
	/** Primitive args: literal values (baked into GLSL) or ParamRefs (uniform-backed). */
	args: {[argKey: string]: LayerArg} = {};
	blend: BlendMode = "over";
	/** Blend-mode args (e.g. heightBlend softness). Values may be ParamRefs. */
	blendArgs: {[argKey: string]: LayerArg} = {};
	contributes: LayerContribution = "both";
	/** Set11 Phase 2 — optional: this layer's own roughness (0..1).  When set, it replaces the accumulated roughness wherever the layer's colour is applied — using the SAME weight the colour blend used, so roughness and albedo always agree about which layer "owns" a fragment.  Only active under the `roughnessAwareSpecular` assemble flag. */
	roughnessOut?: LayerArg;
}

/** A recipe: either a root definition, or an `extend` of another recipe (self-referential, D10).
 *  On a root: `layers` is the full stack. On an `extend`: APPEND-only additions.
 *  Structural edits beyond append/disable require `fork` (see RecipeResolver.forkRecipe). */
export class MaterialRecipe {
	constructor(data?: Partial<MaterialRecipe>) { Object.assign(this, data); }
	id = "";
	name = "";
	/** n = root recipe (built-in preset, or a fork's snapshot). Set = live `extend` link. */
	baseRecipeID?: string;

	/** [for derived recipes] Layer ids (from anywhere in the base chain) to suppress. */
	disabledLayerIDs: string[] = [];
	/** [for derived recipes] Escape hatch: keyed "layerId:argKey". Reaches args the base author didn't expose. Fragile by design. */
	layerArgOverrides: {[addr: string]: LayerArg} = {};
	/** Layers added by this recipe. */
	layers: MaterialLayer[] = [];

	/** [for derived recipes] Safe tier: keyed by the base's declared `params` keys. Derived wins. */
	paramOverrides: {[paramKey: string]: ParamValue} = {};
	/** Params added by this recipe. Drives GLSL arg binding AND the auto-generated UI. */
	params: ParamSchema[] = [];

	/** Option D — the per-recipe macro modulator (see {@link MacroModConfig}); absent/null = none (g_macroMod stays 0.0, every ModRef collapses to its base, GLSL byte-identical to a recipe with no modulation — existing D1 cache keys stay valid). */
	macroMod?: MacroModConfig|n;

	/** MaterialSample.roughness output (D6). Per-recipe constant for Phase 2.
	 *  null = inherit from the base chain (resolved fallback: 0.85). */
	roughness?: number|n;

	/** Per-recipe MULTIPLIER on the global `u_specStrength` specular lobe (0 = this material opts out
	 *  of specular entirely; 1 = full).  Distinct from `roughness`, which sets the lobe's WIDTH and
	 *  has no "off" value: roughness 1.0 yields exponent 1 (a broad wash), not zero specular.
	 *  null = inherit from the base chain (resolved fallback: 1). */
	specStrength?: number|n;

	/** When true, lighting uses a STRAIGHT-UP geometric normal instead of the per-sample u_geoNormal
	 *  lattice — so only the material's own detail normal shades the surface.  For LEVEL materials
	 *  (water): a lake surface has no large-scale slope, so inheriting terrain slope is wrong on the
	 *  merits, AND u_geoNormal comes from the camera-dependent working-grid solve, whose sub-degree
	 *  wobble a tight specular lobe amplifies into glints that swim when the camera pans.
	 *  NOTE this also removes terrain-slope HILLSHADE for the material (same normal feeds both).
	 *  null = inherit from the base chain (resolved fallback: false). */
	flatGeoNormal?: boolean|n;

	/** Degrees above the horizon to shade THIS material's light at, overriding the terrain-definition's
	 *  global `light_altitude`. `null` = use the global value.  Azimuth/ambient/strength stay global.
	 *
	 *  WHY: under the fixed top-down view V = +z, so the Blinn-Phong half-vector sits (90 - altitude)/2
	 *  degrees off vertical. At a 30-degree sun that is 15 degrees, i.e. a surface must have ~0.27 slope
	 *  to glint — far steeper than a water recipe's wave micro-slopes (~0.02-0.06). Raising the altitude
	 *  for this material alone brings the half-vector within reach, without flattening the sun angle for
	 *  the surrounding terrain. Equivalent to (and more predictable than) faking the view vector.
	 *  null = inherit from the base chain (else use the global rig). */
	lightAltitudeOverride?: number|n;
}

/** An instance placed on a map: which recipe + its per-instance seed/params. Phase 7. */
export class ProcMaterialDef {
	constructor(data?: Partial<ProcMaterialDef>) { Object.assign(this, data); }
	id = "";
	name = "";
	recipeID = "";
	seed = 0;
	paramOverrides: {[paramKey: string]: ParamValue} = {};
}

/** TS mirror of the GLSL MaterialSample struct (D6) — every material outputs the full sample even
 *  though the Phase 2/3 lighting stage consumes only albedo + normal. */
export interface MaterialSampleTS {
	albedo: [number, number, number];
	normal: [number, number, number];
	roughness: number;
	height: number;
}

/** GLSL struct every material eval returns (D6). */
export const MATERIAL_SAMPLE_GLSL = `struct MaterialSample { vec3 albedo; vec3 normal; float roughness; float height; };`;

/** Max ramp stops per ramp param (each stop = one vec4: t, r, g, b). Must match the GLSL `vec4 stops[6]` sizing. */
export const RAMP_MAX_STOPS = 6;
/** Max octaves in fbmVal (constant loop bound — GLSL ES 300 portability). */
export const MAX_OCTAVES = 8;
